import { Component, Input, OnInit, ChangeDetectorRef } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { ProjectModel } from "app/models/project.model";
import { DeviceOperationStatus, DeviceOperationType, ProjectDeviceModel } from "app/models/projectDevice.model";
import { DeviceService } from "app/services/api/device.service";
import { ManufacturerService } from "app/services/api/manufacturer.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ToastrService } from "ngx-toastr";
import { locale as catalan } from "../../../i18n/ca";
import { locale as english } from "../../../i18n/en";
import { locale as spanish } from "../../../i18n/es";
import { forkJoin } from "rxjs";
import { ProjectDeviceAddEditComponent } from "../project-device-add-edit/project-device-add-edit.component";

@Component({
    selector: "app-project-devices",
    templateUrl: "./project-devices.component.html",
    styleUrls: ["./project-devices.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class ProjectDevicesComponent implements OnInit {
    private readonly svantekProtocolName = "direct";
    private readonly svantekManufacturerName = "Svantek";
    /** Map of manufacturer id to name (from API), used for Svantek detection. */
    private manufacturerNameById: Record<string, string> = {};

    @Input()
    project: ProjectModel;
    private dialogRef: MatDialogRef<ProjectDeviceAddEditComponent>;

    public devices: ProjectDeviceModel[] = [];

    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    confirmString: string;

    public displayedColumns = ["name", "image", "address", "comment", "active", "buttons"];

    constructor(
        private readonly projectDeviceService: ProjectDeviceService,
        private readonly manufacturerService: ManufacturerService,
        private readonly deviceService: DeviceService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _matDialog: MatDialog,
        private toastr: ToastrService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
    }

    ngOnInit() {
        if (!this.project?.id) {
            return;
        }
        this.loadData();
    }

    loadData() {
        if (!this.project?.id) {
            return;
        }
        forkJoin({
            devices: this.projectDeviceService.getAll(this.project.id),
            manufacturers: this.manufacturerService.getAll(),
        }).subscribe(
            ({ devices, manufacturers }) => {
                this.devices = devices;
                this.manufacturerNameById = manufacturers.reduce((acc, m) => ({ ...acc, [m.id]: m.name }), {} as Record<string, string>);
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );
    }

    activateDevice(device: ProjectDeviceModel) {
        this.deviceService.geActivated(device.baseDevice).subscribe((activeDevice) => {
            if (activeDevice && activeDevice.projectDeviceId) {
                const message = this._fuseTranslationLoaderService.translateService.instant("DEVICES.ConfirmActivate", {
                    project: activeDevice.projectName,
                    deviceName: activeDevice.projectDevicename ? activeDevice.projectDevicename : activeDevice.deviceName,
                });
                this.confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
                    disableClose: false,
                });
                this.confirmDialogRef.componentInstance.confirmMessage = message;
                this.confirmDialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        this.activateDeviceAction(device);
                    }
                    this.confirmDialogRef = null;
                });
            } else {
                this.activateDeviceAction(device);
            }
        });
    }

    private activateDeviceAction(device: ProjectDeviceModel) {
        this.projectDeviceService.activate(device.id).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    deactivateDevice(device: ProjectDeviceModel) {
        this.projectDeviceService.deactivate(device.id).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    deleteDevice(device: ProjectDeviceModel) {
        this.projectDeviceService.delete(device.id).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    isSvantekDevice(device: ProjectDeviceModel): boolean {
        const manufacturerId = device?.manufacturer;
        const manufacturerName = manufacturerId ? this.manufacturerNameById[manufacturerId] : undefined;
        if (manufacturerName && manufacturerName.toLowerCase() === this.svantekManufacturerName.toLowerCase()) {
            return true;
        }
        return device?.baseDeviceData?.protocol?.name?.toLowerCase() === this.svantekProtocolName;
    }

    hasPendingOperation(device: ProjectDeviceModel): boolean {
        return (
            device?.operation?.status === DeviceOperationStatus.Pending || device?.operation?.status === DeviceOperationStatus.InProgress
        );
    }

    getPendingOperationStatusLabel(device: ProjectDeviceModel): string {
        if (device?.operation?.status === DeviceOperationStatus.InProgress) {
            return "DEVICES.OperationInProgress";
        }
        return "DEVICES.OperationPending";
    }

    getPendingOperationTypeLabel(device: ProjectDeviceModel): string {
        if (device?.operation?.type === DeviceOperationType.Reset) {
            return "DEVICES.OperationTypeReset";
        }
        return "DEVICES.OperationTypeGeneric";
    }

    resetDevice(device: ProjectDeviceModel) {
        if (this.hasPendingOperation(device)) {
            return;
        }

        const confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
            disableClose: false,
        });
        confirmDialogRef.componentInstance.confirmMessage = this._fuseTranslationLoaderService.translateService.instant(
            "DEVICES.ConfirmReset",
            {
                deviceName: device.deviceName,
            },
        );
        confirmDialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.projectDeviceService.reset(device.id).subscribe(
                (response: { status: "queued" | "already_pending" | "rate_limited"; retryAfterSeconds?: number }) => {
                    if (response.status === "queued") {
                        this.toastr.success(this._fuseTranslationLoaderService.translateService.instant("DEVICES.ResetQueued"));
                    } else if (response.status === "already_pending") {
                        this.toastr.info(this._fuseTranslationLoaderService.translateService.instant("DEVICES.ResetAlreadyPending"));
                    } else {
                        this.toastr.warning(
                            this._fuseTranslationLoaderService.translateService.instant("DEVICES.ResetRateLimited", {
                                seconds: response.retryAfterSeconds ?? 0,
                            }),
                        );
                    }
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    addEditDevice(device?: ProjectDeviceModel): void {
        const data = device
            ? { action: "edit", device, project: this.project }
            : { action: "new", devices: this.devices, project: this.project };
        this.dialogRef = this._matDialog.open(ProjectDeviceAddEditComponent, {
            data,
            panelClass: "right-panel-dialog",
            width: "50vw",
            maxWidth: "100vw",
            height: "100%",
            maxHeight: "100%",
            position: { right: "0", top: "0", bottom: "0" },
            hasBackdrop: true,
            disableClose: false,
        });

        this.dialogRef.afterClosed().subscribe((response: any) => {
            if (!response) {
                return;
            }
            const addEditDevice = response.device;
            const file = response.file;
            const { editImage } = response;
            const success = () => this.loadData();
            const fail: (error: any) => void = (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                if (error.error?.statusCode === 404) {
                    this.router.navigate(["/admin/projects"]);
                }
            };
            if (response.action === "new") {
                if (!file) {
                    this.projectDeviceService.create(this.project.id, addEditDevice).subscribe(() => success(), fail);
                    return;
                }
                this.projectDeviceService.create(this.project.id, addEditDevice).subscribe((projectDevice: ProjectDeviceModel) => {
                    if (file) {
                        this.projectDeviceService.addImage(projectDevice.id, file).subscribe(() => success(), fail);
                    } else {
                        success();
                    }
                }, fail);
            } else if (response.action === "edit") {
                const op: ReturnType<ProjectDeviceService["edit"]>[] = [this.projectDeviceService.edit(this.project.id, addEditDevice)];
                if (editImage) {
                    if (file) {
                        op.push(this.projectDeviceService.addImage(addEditDevice.id, file) as ReturnType<ProjectDeviceService["edit"]>);
                    } else {
                        op.push(this.projectDeviceService.removeImage(addEditDevice.id) as ReturnType<ProjectDeviceService["edit"]>);
                    }
                }
                forkJoin(op).subscribe(() => success(), fail);
            }
        });
    }
}
